import { fakerES_MX as faker } from '@faker-js/faker'
import {
  DC3_THEMATIC_AREAS,
  resolveDc3ThematicArea,
} from 'config/dc3ThematicAreas'
import {
  IDc3AttestationTemplateData,
  IDc3CertificateData,
  IDc3ExecutionPeriod,
} from './types'

const randomThematicArea = () => {
  const area = faker.helpers.arrayElement([...DC3_THEMATIC_AREAS])
  return { code: area.code, name: area.name }
}

const randomCurp = () => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const digits = '0123456789'
  const pick = (source: string, length: number) =>
    Array.from(
      { length },
      () => source[Math.floor(Math.random() * source.length)]
    ).join('')

  return `${pick(letters, 4)}${pick(digits, 6)}${pick(letters, 6)}${pick(
    digits,
    2
  )}`
}

const randomRfc = () => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const digits = '0123456789'
  const pick = (source: string, length: number) =>
    Array.from(
      { length },
      () => source[Math.floor(Math.random() * source.length)]
    ).join('')

  return `${pick(letters, 3)}${pick(digits, 6)}${pick(letters, 3)}`
}

const randomPatronalRegistration = () => {
  const letter = faker.helpers.arrayElement(
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
  )
  return `${letter}${faker.string.numeric(10)}`
}

const randomExecutionPeriod = (): IDc3ExecutionPeriod => {
  const start = faker.date.past({ years: 1 })
  const finish = faker.date.between({ from: start, to: new Date() })

  return {
    startDate: start.toISOString(),
    finishDate: finish.toISOString(),
  }
}

interface BuildDummyDataParams {
  attestation?: Partial<IDc3AttestationTemplateData>
  course?: Partial<{ name: string; hours: number }>
}

const resolveCourseHours = (hours: number | undefined) => {
  if (hours == null || Number.isNaN(Number(hours))) {
    return faker.number.float({ min: 4, max: 40, fractionDigits: 1 })
  }

  return Number(hours)
}

/** Datos dummy de preview; las firmas se resuelven en generateDc3Pdf vía signatureOverrides. */
export const buildDc3DummyData = ({
  attestation = {},
  course = {},
}: BuildDummyDataParams = {}): IDc3CertificateData => {
  const thematicArea =
    resolveDc3ThematicArea(attestation.thematicArea) || randomThematicArea()

  return {
    attestation: {
      companyFiscalName: attestation.companyFiscalName || faker.company.name(),
      companyRfc: attestation.companyRfc || randomRfc(),
      patronalRegistration:
        attestation.patronalRegistration || randomPatronalRegistration(),
      companyActivity:
        attestation.companyActivity || faker.company.catchPhrase(),
      trainingAgentName:
        attestation.trainingAgentName || faker.person.fullName(),
      trainingAgentStpsRegistration:
        attestation.trainingAgentStpsRegistration ||
        faker.string.alphanumeric(12).toUpperCase(),
      thematicArea,
      instructorName: attestation.instructorName || faker.person.fullName(),
      instructorSignatureUrl: attestation.instructorSignatureUrl ?? null,
      legalRepresentativeName:
        attestation.legalRepresentativeName || faker.person.fullName(),
      legalRepresentativeSignatureUrl:
        attestation.legalRepresentativeSignatureUrl ?? null,
      workersRepresentativeName:
        attestation.workersRepresentativeName || faker.person.fullName(),
      workersRepresentativeSignatureUrl:
        attestation.workersRepresentativeSignatureUrl ?? null,
    },
    course: {
      name: course.name || faker.lorem.words({ min: 3, max: 6 }),
      hours: resolveCourseHours(course.hours),
    },
    worker: {
      fullName: faker.person.fullName(),
      curp: randomCurp(),
      occupation: `${faker.string.numeric(4)} ${faker.person.jobTitle()}`,
      position: faker.person.jobTitle().toUpperCase(),
    },
    executionPeriod: randomExecutionPeriod(),
  }
}

export const splitDateParts = (value: string | null | undefined) => {
  if (!value) {
    return { year: '', month: '', day: '' }
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return { year: '', month: '', day: '' }
  }

  return {
    year: String(date.getFullYear()),
    month: String(date.getMonth() + 1).padStart(2, '0'),
    day: String(date.getDate()).padStart(2, '0'),
  }
}

export const splitCharacters = (
  value: string,
  length: number,
  options?: { contentOnly?: boolean }
) => {
  const normalized = (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '')

  if (options?.contentOnly) {
    return normalized.slice(0, length).split('')
  }

  return Array.from({ length }, (_, index) => normalized[index] || '')
}
